import { Alert } from 'components/atoms/Alert';
import { FreeLoader } from 'components/atoms/Loader/Loader.component';
import { Helper } from 'components/molecules/Helper';
import { includesPattern } from 'components/organisms/CategoriesPicker/utils';
import dayjs from 'dayjs';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useFeatures from 'hooks/useFeatures';
import { last, omit } from 'lodash';
import { default as pick, default as _pick } from 'lodash/pick';
import _uniqueId from 'lodash/uniqueId';
import type { FC } from 'react';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ExpenseCategoryType,
  ExpenseEvidenceReasonEnum,
  ExpenseFormNames,
  ExpenseGuessedFromTypesEnum,
  ExpensePaymentStatusType,
  ExpenseRecurrenceStatus,
  ExpenseVATRegime,
  IExpenseFormValuesV2,
  IExpenseItemV2,
  IExpenseV2,
  PartialIExpenseV2,
  ReviewStatusEnum,
} from 'types/expenses.types';
import { CurrenciesEnum, Nullable, VATReturnFrequency } from 'types/global.types';
import type { Transaction, TransactionAsOption } from 'types/transactions.types';
import type { IUser } from 'types/users.types';
import { AccountTypeEnum, AvailableCountries } from 'types/users.types';
import type { IVehicle } from 'types/vehicles.types';
import { applySuggestedChanges } from 'utils/applySuggestedChanges';
import * as cates from 'utils/categories';
import { isExpert, isWeb } from 'utils/constants';
import { cleanEmptyValues_stringified, getShortAccountType } from 'utils/helpers';
import { getVATAmountFromAmountInclVAT, round } from 'utils/math';
import type useFormMeta from './useFormMeta';

export const isBusinessCar = (categoryId: string | undefined) => {
  if (!categoryId) return false;
  return cates.businessCarDE.includes(categoryId);
};

// export const isCarCategory = (categoryId: string | undefined) => {
//   if (!categoryId) return false;
//   return cates.catesThatRequiresVehicleBE.includes(categoryId);
// };

export const is35To100VATDeductCarCategory = (categoryId: string | undefined) => {
  if (!categoryId) return false;
  return cates.catesWith35To100VATDeductBE.includes(categoryId);
};

export const isCarDependentIncomeDeductCategory = (categoryId: string | undefined) => {
  if (!categoryId) return false;
  return cates.catesWithCarDependentIncomeDeductBE.includes(categoryId);
};

export const isUtilityVehicle = (categoryId: string | undefined) => {
  if (!categoryId) return false;
  return cates.utilityVehiclesBE.includes(categoryId);
};

export const isVehicleFineCategory = (categoryId: string | undefined) => {
  if (!categoryId) return false;
  return cates.vehiclesFinesBE.includes(categoryId);
};

export const isCustomFormCategory = (categoryId: string | undefined) => {
  if (!categoryId) return false;
  return cates.customFormCategories.includes(categoryId);
};

// export const getTotalVATAmount = (items: IExpense['items']): number | null =>
//   items.reduce((acc: number | null, item) => {
//     if (acc == null) return item.VATAmount ?? null;
//     return acc + (item.VATAmount || 0);
//   }, null);

type UseVATRatesOptions = {
  expenseDate: string | undefined;
};

// const customerCountry = useCustomerCountry();
export const useVATRatesGetter = () => {
  const emptyRates = useRef([]);
  const VATRates = useFeatures('expenses.vat_rates') || emptyRates.current;
  const VATRatesDeductible2020 = useFeatures('expenses.vat_rates_deductible_2020') || emptyRates.current;

  return useCallback(
    (options: UseVATRatesOptions): number[] => {
      if (options.expenseDate && new Date(options.expenseDate).getFullYear() === 2020) {
        return VATRates.concat(VATRatesDeductible2020).sort();
      }
      return VATRates;
    },
    [VATRates, VATRatesDeductible2020],
  );
};

export const getUpdatedIncomeTaxDeductibility = (
  expenseDate: string | undefined,
  item: IExpenseFormValuesV2['items'][number],
  shouldUseVehicleData: boolean,
) => {
  if (shouldUseVehicleData) {
    let incomeTaxDeductibility = item.vehicle?.incomeTaxDeductibility;

    if (expenseDate && item.vehicle?.incomeTaxDeductibilityOverrides) {
      const expenseDateNumber = +expenseDate.replace(/-/g, '');
      item.vehicle.incomeTaxDeductibilityOverrides.forEach(([date, deductibility]) => {
        if (expenseDateNumber >= +date) incomeTaxDeductibility = deductibility;
      });
    }

    return incomeTaxDeductibility;
  }
  return item.category?.incomeTaxDeductibility ?? item.incomeTaxDeductibility;
};

export const getUpdatedMaxDeductibleVAT = (item: IExpenseFormValuesV2['items'][number], shouldUseVehicleData: boolean) => {
  if (shouldUseVehicleData) return item.vehicle?.VATDeductibility;
  return item.category?.maxDeductibleVAT ?? item.maxDeductibleVAT;
};

export const getVehicleIncomeTaxDeductibilityPerYear = (vehicle: IVehicle, year: number) => {
  const dateNumber = year * 1_00_00 + 1_01;
  let { incomeTaxDeductibility } = vehicle;

  if (!vehicle.incomeTaxDeductibilityOverrides) return incomeTaxDeductibility;
  for (const [date, deductibility] of vehicle.incomeTaxDeductibilityOverrides) {
    if (dateNumber >= date) {
      incomeTaxDeductibility = deductibility;
    }
  }
  return incomeTaxDeductibility;
};
//

export const expenseAlternativeTransactionsOptions = [
  {
    label: 'transaction.match_remainder_with_cash',
    value: ExpenseFormNames.cash,
    preset: true,
  },
  {
    label: 'transaction.match_with_other_accounts',
    value: ExpenseFormNames.other_accounts,
    preset: true,
  },
];

export const initValuesKeys: (keyof IExpenseV2)[] = [
  '_id',
  'items',
  'supplier',
  'notes',
  'payments',
  '_matchedTransactions',
  'user',
  'file',
  'isInvoice',
  'isValidated',
  'isFake',
  'period',
  'taxPeriod',
  'accountantReview',
  'sufficientlyDocumented',
  'expenseDateNumber',
  'currency',
  'currencyRate',
  'guessed_data',
  'recurrence',
];

const itemsCurrencyKeys: (keyof IExpenseItemV2)[] = [
  'currencyAmount',
  'VATRate',
  'VATAmount',
  'localVATRate',
  'maxDeductibleVAT',
  'incomeTaxDeductibility',
  '_id',
  'professionalPart',
  'amortizationPeriod',
  'category',
  'VATRegime',
  'deduction',
  'isAsset',
  'name',
  'tripTotalKm',
  'tripType',
  'tripVehicleType',
  'tripWorkingDays',
  'vehicle',
];

export const itemDefault = {
  professionalPart: 1,
};

const getDefaultItems = (items?: Partial<IExpenseItemV2>[]): Partial<IExpenseItemV2>[] => {
  if (Array.isArray(items)) {
    const result = items.filter(Boolean);
    if (result.length) return result;
    return [itemDefault];
  }
  return [itemDefault];
};

export const getDefaultValues = (values: PartialIExpenseV2, user: Pick<IUser, 'country' | 'VATType' | 'VATReturnFrequency'>): IExpenseFormValuesV2 => {
  const isCreditNote = values.isCreditNote;

  const currency = values.currency || CurrenciesEnum.EUR;
  const isForeignCurrency = currency !== CurrenciesEnum.EUR;

  const isEditing = !!values?._id;
  const isValidated = !!values?.isValidated;

  const isEditingInvoice = isEditing && isValidated && values?.isInvoice;

  const expenseDateNumber = values.expenseDateNumber || +dayjs().format('YYYYMMDD');

  return {
    ..._pick(values, initValuesKeys),
    expenseDate: dayjs(`${expenseDateNumber}`, 'YYYYMMDD').format('YYYY-MM-DD'),
    currency,
    currencyRate: isForeignCurrency ? values.currencyRate || 1 : 1,
    _frontendId: values._frontendId || values._id || _uniqueId(),
    accountantReview: isWeb
      ? values.accountantReview
      : {
          comments: values.accountantReview?.comments || '',
          reviewStatus: values.accountantReview?.reviewStatus || ReviewStatusEnum.notReviewd,
        },
    isInvoice: values.isInvoice ?? user.country === AvailableCountries.germany,
    file: values.file?.path
      ? {
          path: values.file.path,
          hash: values.file.hash || null,
          name: values.file.name || values.file.path.split('/').pop(),
        }
      : null,
    recurrence: values.recurrence?.status === ExpenseRecurrenceStatus.ongoing ? values.recurrence : null,
    payments: {
      cash: values.payments?.cash || false,
      other_accounts: values.payments?.other_accounts || false,
      transactions: values._matchedTransactions || [],
      status: {
        type: values.payments?.status?.type || ExpensePaymentStatusType.paid,
        payment_date: values.payments?.status?.payment_date,
        due_date: values.payments?.status?.due_date,
      },
    },
    items: getDefaultItems(values.items).map((item) => {
      const currencyAmount = item.currencyAmount != null ? round(item.currencyAmount) : item.currencyAmount;
      let VATRate = item.VATRate;
      let category = item.category;

      if (isEditingInvoice && category && category.allowsVAT === false) {
        if (item.VATRegime && item.VATRegime !== ExpenseVATRegime.exempt_item) {
          category = {
            ...category,
            allowsVAT: true,
          };
        }
      }

      const baseCurrencyAmount = item.baseCurrencyAmount != null ? round(item.baseCurrencyAmount) : item.baseCurrencyAmount;
      const VATAmount = item.VATAmount;

      if (isEditing) {
        if (baseCurrencyAmount != null && VATRate != null && VATAmount != null) {
          const expectedVATAmount = getVATAmountFromAmountInclVAT(baseCurrencyAmount, VATRate);
          const diff = Math.abs(expectedVATAmount - VATAmount /* could be legacy and different from the new VATAmount */);
          if (diff > 0.1) VATRate = null;
        }
      }

      return {
        ..._pick(item, itemsCurrencyKeys),
        VATAmount,
        VATRate,
        currencyAmount: isCreditNote && currencyAmount != null ? currencyAmount * -1 : currencyAmount,
        category,
        amortizationPeriod: isValidated ? item.amortizationPeriod || 0 : item.amortizationPeriod || null,
      };
    }),
    user: {
      VATReturnFrequency: user.VATReturnFrequency as VATReturnFrequency,
      VATType: values.user?.VATType || user.VATType,
      country: values.user?.country || user.country,
    },
    // isSuggestedPeriod: values.isSuggestedPeriod,
  };
};

export const adaptExpenseWithMeta = (expense: IExpenseFormValuesV2, metaAPI: ReturnType<typeof useFormMeta>): IExpenseFormValuesV2 => {
  // const formMeta = metaAPI.getFormMeta(expense);
  return applySuggestedChanges(
    {
      ...expense,
      // isThereVATOnInvoice:
      //   expense.isValidated ||
      //   !formMeta.fields.isThereVATOnInvoice.isApplicable ||
      //   expense.guessedIsVATPresent == null
      //     ? undefined
      //     : expense.guessedIsVATPresent,
    },
    metaAPI.getSuggestedChanges(expense),
  );
};

export const renderLoader = (className = '') => <FreeLoader className={`text-primary-200 fill-primary-400 ${className}`} />;

export const supplierNameToSearchValue = (name: string | undefined) => {
  if (!name) return;
  // try to get "first" word with 4 or more char otherwise get the second longest one.
  const result = name.split(' ').reduce((acc, str) => {
    if (acc.length >= 4) return acc;
    if (str.length > acc.length) return str;
    return acc;
  }, '');
  if (result) return result;
};

type GetDefaultVATRateOptions = {
  // isSupplierLocal: boolean;
  isCustomerBE: boolean;
  expenseDate: string | undefined | null;
  categorySuggestedVATRate: number | undefined | null;
};
export const getDefaultVATRate = (options: GetDefaultVATRateOptions) => {
  if (options.categorySuggestedVATRate === null) return null;

  const isValidVATRate = typeof options.categorySuggestedVATRate === 'number';
  // if (options.isSupplierLocal) {
  // 21% for belgian users
  if (options.isCustomerBE) {
    if (isValidVATRate) return options.categorySuggestedVATRate;
    return 0.21;
  }
  // 19% for German users (16% for users if expenses is in 4th quarter)
  const date = options.expenseDate ? dayjs(options.expenseDate) : null;
  if (date?.year() === 2020 && date?.quarter() === 4) {
    if (options.categorySuggestedVATRate === 0) return 0;
    if (options.categorySuggestedVATRate === 0.07) return 0.05;
    return 0.16;
  }
  if (isValidVATRate) return options.categorySuggestedVATRate;
  return 0.19;
  // }
  // 0% for intra-extra eu
  // return 0;
};

export const currentYear = new Date().getFullYear();

export const vehiclePurchaseDateMin = dayjs(new Date('1900-01-01')).startOf('day').toDate();
export const vehiclePurchaseDateMinAsNumber = 19000101;
export const vehiclePurchaseDateMax = new Date();
export const vehiclePurchaseDateMaxAsNumber = +dayjs().format('YYYYMMDD');

const ambiguousSuppliers = [
  'Spotify',
  'Netflix',
  'Delhaize',
  'Luminus',
  'Engie',
  'Carrefour',
  'Lidl',
  'Aldi',
  'Aldi Süd',
  'Aldi Nord',
  'Netto Marken-Discount',
  'Spar',
  'Edeka',
  'Colruyt',
  'Rewe',
  'Penny Markt',
].map((s) => s.toLowerCase());

export const isAmbiguousSupplier = (name: string | undefined) => {
  if (!name) return false;
  return ambiguousSuppliers.includes(name.toLowerCase());
};

const scamySuppliers = ['SDI', 'SDZ', 'Unizo'].map((s) => s.toLowerCase());

export const isScamySupplier = (name: string | undefined) => {
  if (!name) return false;
  return scamySuppliers.includes(name.toLowerCase());
};

// TODO: move to categories file
const tenDaysRuleCategories = [
  'de.legal_and_other_fees.accounting_fees',
  'de.legal_and_other_fees.other_fees',
  'de.legal_and_other_fees.audit_and_closing_fees',
  'de.vehicle.leasing',
  'de.vehicle.insurance',
  'de.vehicle.taxes',
  'de.vehicle.road_and_registration_taxes',
  'de.technology.hosting',
  'de.technology.maintenance',
  'de.phone_and_internet.phone',
  'de.phone_and_internet.internet',
  'de.taxes_and_insurance.vat_payment',
  'de.taxes_and_insurance.for_self_employed.private_insurances',
  'de.taxes_and_insurance.for_self_employed.healthinsurance',
  'de.taxes_and_insurance.for_self_employed.pension_plan',
  'de.taxes_and_insurance.for_self_employed.trade_tax',
  'de.taxes_and_insurance.for_self_employed.import_fees',
  'de.taxes_and_insurance.for_self_employed.fines_for_late_payment',
  'de.taxes_and_insurance.for_self_employed.nd_fines_for_late_payment',
  'de.taxes_and_insurance.for_self_employed.income_tax',
  'de.taxes_and_insurance.for_self_employed.solidariy_charge',
  'de.taxes_and_insurance.for_self_employed.churchtax',
  'de.taxes_and_insurance.for_self_employed.property_tax.outside',
  'de.taxes_and_insurance.for_self_employed.property_tax.inside_main',
  'de.taxes_and_insurance.for_self_employed.property_tax.inside_secondary',
  'de.subcontracting.displayName', // remove later after backend deploys german categories changes
  'de.subcontracting',
  'de.workplace.rent.outside',
  'de.workplace.maintenance.outside',
  'de.workplace.maintenance.inside_main',
  'de.workplace.maintenance.inside_secondary',
  'de.workplace.rent.homelumpsum',
];

export const doesTenDayRuleApplies = (expenseDate: string | undefined, categoryIds: (string | undefined)[] | undefined) => {
  if (!expenseDate || !categoryIds) return false;
  const date = dayjs(expenseDate);
  if (!date.isValid()) return false;
  const dayOfTheMonth = date.get('D');
  return date.month() === 0 && dayOfTheMonth >= 1 && dayOfTheMonth <= 10 && categoryIds.some((id) => id && tenDaysRuleCategories.includes(id));
};

export const getLockStatusBody = (expense: IExpenseFormValuesV2, customerCountry: AvailableCountries) => {
  const user = {
    VATType: expense.user?.VATType || undefined,
    VATReturnFrequency: expense.user?.VATReturnFrequency || undefined,
  };

  if (customerCountry === AvailableCountries.germany) {
    return {
      country: AvailableCountries.germany,
      paymentDate: expense.payments?.status?.payment_date || undefined,
      period: expense.period || undefined,
      status: expense.payments?.status?.type || undefined,
      user,
    };
  }

  return {
    country: AvailableCountries.belgium,
    expenseDate: expense.expenseDate || undefined,
    period: expense.period || undefined,
    user,
  };
};

export const TaxSearchWarning: FC<{ searchTerm?: string }> = ({ searchTerm }) => {
  const user = useGetCurrentCustomerData().data as IUser;
  const { t } = useTranslation();

  if (!searchTerm?.trim()) return null;

  if (user.country === AvailableCountries.belgium) {
    const taxesOrVATMatch = includesPattern({
      searchValue: searchTerm,
      keywords: [t('vat'), t('tax')],
    });

    if (!taxesOrVATMatch) return null;

    const shortAccountType = getShortAccountType(user.accountType);
    if (shortAccountType === AccountTypeEnum.natural_person) {
      return (
        <Alert
          type="warning"
          className="mb-0"
          name="be natural person search for tax category in expense form"
          description={
            <div>
              {t('expenses.warning_bubble.search_for_tax_category.natural.title.be')}
              {'  '}
              <Helper className="!inline">
                <Helper.Paragraph>{t('expenses.warning_bubble.search_for_tax_category.natural.text.be')}</Helper.Paragraph>
                <Helper.Paragraph>{t('expenses.warning_bubble.search_for_tax_category.natural.text2.be')}</Helper.Paragraph>
                <Helper.Paragraph>{t('expenses.warning_bubble.search_for_tax_category.natural.text3.be')}</Helper.Paragraph>
                <Helper.Paragraph>{t('expenses.warning_bubble.search_for_tax_category.natural.text4.be')}</Helper.Paragraph>
              </Helper>
            </div>
          }
        />
      );
    }
    if (shortAccountType === AccountTypeEnum.one_person_company) {
      return (
        <Alert
          type="warning"
          className="mb-0"
          name="be one person company search for tax category in expense form"
          description={
            <div>
              {t('expenses.warning_bubble.search_for_tax_category.company.title.be')}
              {'  '}
              <Helper className="!inline">
                <Helper.Paragraph>{t('expenses.warning_bubble.search_for_tax_category.company.text.be')}</Helper.Paragraph>
                <Helper.Paragraph>{t('expenses.warning_bubble.search_for_tax_category.company.text2.be')}</Helper.Paragraph>
                <Helper.Paragraph>{t('expenses.warning_bubble.search_for_tax_category.company.text3.be')}</Helper.Paragraph>
                <Helper.Paragraph>{t('expenses.warning_bubble.search_for_tax_category.company.text4.be')}</Helper.Paragraph>
              </Helper>
            </div>
          }
        />
      );
    }
  }

  if (user.country === AvailableCountries.germany) {
    const taxKeywordsMatch = includesPattern({
      searchValue: searchTerm,
      keywords: [t('tax'), t('income_tax')],
    });
    if (taxKeywordsMatch) {
      return (
        <Alert
          type="warning"
          className="mb-0"
          name="de search for tax category in expense form"
          description={
            <div>
              {t('expenses.warning_bubble.search_for_tax_category.title.de')}
              {'  '}
              <Helper className="!inline">
                <Helper.Paragraph>{t('expenses.warning_bubble.search_for_tax_category.text.de')}</Helper.Paragraph>
                <Helper.Paragraph>{t('expenses.warning_bubble.search_for_tax_category.text2.de')}</Helper.Paragraph>
                <Helper.Paragraph>{t('expenses.warning_bubble.search_for_tax_category.text3.de')}</Helper.Paragraph>
                <Helper.Paragraph>{t('expenses.warning_bubble.search_for_tax_category.text4.de')}</Helper.Paragraph>
              </Helper>
            </div>
          }
        />
      );
    }
    const healthOrPensionMatch = includesPattern({
      searchValue: searchTerm,
      keywords: [t('keywords.keyword_pension'), t('keywords.keyword_pension_plan'), t('keywords.keyword_health')],
    });
    if (healthOrPensionMatch) {
      return (
        <Alert
          type="warning"
          className="mb-0"
          name="de search for tax category in expense form"
          description={
            <div>
              {t('expenses.warning_bubble.search_personal_categories.title.de')}
              {'  '}
              <Helper className="!inline">
                <Helper.Paragraph>{t('expenses.warning_bubble.search_personal_categories.text1.de')}</Helper.Paragraph>
                <Helper.Paragraph>{t('expenses.warning_bubble.search_personal_categories.text2.de')}</Helper.Paragraph>
                <Helper.Paragraph>{t('expenses.warning_bubble.search_personal_categories.text3.de')}</Helper.Paragraph>
              </Helper>
            </div>
          }
        />
      );
    }
    // const healthInsuranceMatch = includesPattern({
    //   searchValue: searchTerm,
    //   keywords: ['health insurance', 'Krankenkasse', 'Krankenkassenbeitrag'],
    // });

    // if (healthInsuranceMatch) {
    //   return (
    //     <Alert
    //       type="warning"
    //       className="mb-0"
    //       name="de user searching for health insurance in categories"
    //       description={t('expenses.warning_bubble.health_insurance.categories_search.de')}
    //     />
    //   );
    // }
  }

  return null;
};

const formatExpenseForSubmit = (expense: IExpenseFormValuesV2) => {
  const bodyKeys: (keyof IExpenseFormValuesV2)[] = [
    'expenseDate',
    'items',
    'supplier',
    'notes',
    'payments',
    'user',
    'file',
    // 'isCreditNote',
    'isInvoice',
    'isValidated',
    'isFake',
    'period',
    'taxPeriod',
    'accountantReview',
    'currency',
    'currencyRate',
    'shouldUpdateTaxStatus',
    'create_recurrence',
  ];

  const itemsKeys: (keyof IExpenseFormValuesV2['items'][number])[] = [
    '_id',
    'currencyAmount',
    'VATRate',
    'maxDeductibleVAT',
    'incomeTaxDeductibility',
    'professionalPart',
    'amortizationPeriod',
    'category',
    'VATRegime',
    'isAsset',
    'name',
    'tripTotalKm',
    'tripType',
    'tripVehicleType',
    'tripWorkingDays',
    'vehicle',
  ];

  const currency = expense.currency || CurrenciesEnum.EUR;
  return {
    ...pick(expense, bodyKeys),
    isCreditNote: false,
    shouldUpdateTaxStatus: !!expense.shouldUpdateTaxStatus,
    create_recurrence: expense.create_recurrence
      ? {
          frequency: expense.create_recurrence.frequency,
          start_date: expense.create_recurrence.start_date,
          end_date: expense.create_recurrence.end_date || null,
          include_file: !!expense.create_recurrence.include_file,
          interval: expense.create_recurrence.interval || undefined,
        }
      : undefined,
    supplier: {
      name: expense.supplier?.name,
    },
    isFake: !!expense.isFake,
    isValidated: isExpert ? expense.isValidated : undefined,
    accountantReview: isExpert
      ? {
          reviewStatus: expense.accountantReview?.reviewStatus || 'not_reviewed',
          comments: expense.accountantReview?.comments?.trim() || null,
        }
      : undefined,
    expenseDateNumber: expense.expenseDate ? +dayjs(expense.expenseDate).format('YYYYMMDD') : undefined,
    expenseDate: undefined,
    currency,
    currencyRate: currency === CurrenciesEnum.EUR ? 1 : expense.currencyRate,
    payments: {
      cash: expense.payments?.cash || false,
      other_accounts: expense.payments?.other_accounts || false,
      transactions: expense.payments?.transactions?.map((t) => t._id) || [],
      status: {
        type: expense.payments?.status?.type || undefined,
        payment_date: expense.payments?.status?.payment_date || undefined,
        due_date: expense.payments?.status?.due_date || undefined,
      },
    },
    file: expense.file?.path
      ? {
          hash: undefined,
          name: expense.file.name || expense.file.path.split('/').pop(),
          path: expense.file.path,
        }
      : null,
    taxPeriod: expense.taxPeriod || null,
    period: expense.period || null,
    notes: expense.notes || null,
    items: expense.items?.map((item) => ({
      ...pick(item, itemsKeys),
      _id: item._id || null,
      amortizationPeriod: item.amortizationPeriod || null,
      name: item.name || null,
      VATRate: item.VATRate ?? null,
      localVATRate: item.localVATRate ?? null,
      tripTotalKm: item.tripTotalKm ?? undefined,
      tripType: item.tripType || undefined,
      tripVehicleType: item.tripVehicleType || undefined,
      tripWorkingDays: item.tripWorkingDays ?? undefined,
      vehicle: item.vehicle || undefined,
    })),
  };
};

export const cleanExpenseDataForSubmit = (expense: IExpenseFormValuesV2) => {
  return formatExpenseForSubmit(expense) as PartialIExpenseV2;
};

export const cleanExpenseDataForTaxImpact = (expense: IExpenseFormValuesV2) => {
  return formatExpenseForSubmit(expense) as PartialIExpenseV2;
};

export const getExpenseLinkedPaymentsOppositeDirectionTotal = (payments?: any[], isCreditNote?: boolean): number =>
  payments
    ?.filter((option: TransactionAsOption) => {
      if (isCreditNote) return option.transaction.baseCurrencyAmount < 0;

      return option.transaction.baseCurrencyAmount > 0;
    })
    .reduce((agg: number, current: TransactionAsOption) => agg + Math.abs(current.transaction.baseCurrencyAmount || 0), 0);

export const getExpenseLinkedPaymentsAmount = (payments?: any[], isCreditNote?: boolean): number =>
  payments
    ?.filter((option: TransactionAsOption) => {
      if (isCreditNote) return option.transaction.baseCurrencyAmount > 0;

      return option.transaction.baseCurrencyAmount < 0;
    })
    .reduce((agg: number, current: TransactionAsOption) => agg + Math.abs(current.transaction.baseCurrencyAmount || 0), 0);

export const isTaxSupplierName = (name: string | undefined | null) => {
  if (typeof name !== 'string') return false;
  const regex = /\b(tax|vat|tva|btw|federale overheid financiën|spf|fod)\b/i;
  return regex.test(name);
};

export enum DisallowedVATRegimesReason {
  is_non_zero_vat_rate = 'is_non_zero_vat_rate', // must select 0% VAT rate to enable this option
  is_non_standard_vat_rate = 'is_non_standard_vat_rate', // must select a standard VAT rate to enable this option
  is_non_foreign_vat_rate = 'is_non_foreign_vat_rate', // must select foreign VAT to enable this option
  is_exempt_vat_category = 'is_exempt_vat_category', // category doesn't allow VAT

  is_not_subject_to_vat = 'is_not_subject_to_vat', // to enable this option you must be subject to VAT
  is_not_proper_invoice = 'is_not_proper_invoice', // this option is enabled for proper invoices
  is_not_proper_invoice_and_is_not_good_category = 'is_not_proper_invoice_and_is_not_good_category', // this option is enabled forgoods categoryies and proper invoices
  is_proper_invoice_and_is_not_service_category = 'is_proper_invoice_and_is_not_service_category', // this option is enabled for proper invoices and service categoryies
  is_not_applicable_for_category = 'is_not_applicable_for_category', // this option is disabled for this specific category
}

export const getVATRegimesStatus = (() => {
  const zeroRateSubjectToVATInvoice = {
    allowed: [
      ExpenseVATRegime.exempt_item,
      ExpenseVATRegime.extra_eu_reverse_charge,
      ExpenseVATRegime.franchisee_supplier,
      ExpenseVATRegime.eu_reverse_charge,
      ExpenseVATRegime.local_reverse_charge,
    ],
    disallowed: {
      [ExpenseVATRegime.standard]: DisallowedVATRegimesReason.is_non_standard_vat_rate,
      [ExpenseVATRegime.foreign_vat]: DisallowedVATRegimesReason.is_non_foreign_vat_rate,
    },
    hidden: [],
  };

  const zeroRateSubjectToVATInvoiceGood = {
    allowed: [ExpenseVATRegime.exempt_item, ExpenseVATRegime.franchisee_supplier, ExpenseVATRegime.eu_reverse_charge, ExpenseVATRegime.local_reverse_charge],
    disallowed: {
      [ExpenseVATRegime.standard]: DisallowedVATRegimesReason.is_non_standard_vat_rate,
      [ExpenseVATRegime.foreign_vat]: DisallowedVATRegimesReason.is_non_foreign_vat_rate,
      [ExpenseVATRegime.extra_eu_reverse_charge]: DisallowedVATRegimesReason.is_proper_invoice_and_is_not_service_category,
    },
    hidden: [],
  };

  const zeroRateSubjectToVATTicketService = {
    allowed: [ExpenseVATRegime.exempt_item, ExpenseVATRegime.franchisee_supplier],
    disallowed: {
      [ExpenseVATRegime.standard]: DisallowedVATRegimesReason.is_non_standard_vat_rate,
      [ExpenseVATRegime.foreign_vat]: DisallowedVATRegimesReason.is_non_foreign_vat_rate,
      [ExpenseVATRegime.local_reverse_charge]: DisallowedVATRegimesReason.is_not_proper_invoice,
      [ExpenseVATRegime.eu_reverse_charge]: DisallowedVATRegimesReason.is_not_proper_invoice,
      [ExpenseVATRegime.extra_eu_reverse_charge]: DisallowedVATRegimesReason.is_not_proper_invoice_and_is_not_good_category,
    },
    hidden: [],
  };

  const zeroRateSubjectToVATTicketGood = {
    allowed: [ExpenseVATRegime.exempt_item, ExpenseVATRegime.extra_eu_reverse_charge, ExpenseVATRegime.franchisee_supplier],
    disallowed: {
      [ExpenseVATRegime.standard]: DisallowedVATRegimesReason.is_non_standard_vat_rate,
      [ExpenseVATRegime.local_reverse_charge]: DisallowedVATRegimesReason.is_not_proper_invoice,
      [ExpenseVATRegime.foreign_vat]: DisallowedVATRegimesReason.is_non_foreign_vat_rate,
      [ExpenseVATRegime.eu_reverse_charge]: DisallowedVATRegimesReason.is_not_proper_invoice,
    },
    hidden: [],
  };

  const zeroRateNonSubjectToVAT = {
    allowed: [ExpenseVATRegime.exempt_item, ExpenseVATRegime.extra_eu_reverse_charge, ExpenseVATRegime.franchisee_supplier, ExpenseVATRegime.eu_reverse_charge],
    disallowed: {
      [ExpenseVATRegime.standard]: DisallowedVATRegimesReason.is_non_standard_vat_rate,
      [ExpenseVATRegime.local_reverse_charge]: DisallowedVATRegimesReason.is_not_subject_to_vat,
      [ExpenseVATRegime.foreign_vat]: DisallowedVATRegimesReason.is_non_foreign_vat_rate,
    },
    hidden: [],
  };

  // ------------IMPORT FEES: CUSTOM FORM-------------------

  const zeroRateSubjectToVATInvoiceCustomImportFees = {
    allowed: [ExpenseVATRegime.exempt_item, ExpenseVATRegime.extra_eu_reverse_charge, ExpenseVATRegime.eu_reverse_charge],
    disallowed: {
      [ExpenseVATRegime.standard]: DisallowedVATRegimesReason.is_non_standard_vat_rate,
      [ExpenseVATRegime.foreign_vat]: DisallowedVATRegimesReason.is_non_foreign_vat_rate,
      [ExpenseVATRegime.franchisee_supplier]: DisallowedVATRegimesReason.is_not_applicable_for_category,
      [ExpenseVATRegime.local_reverse_charge]: DisallowedVATRegimesReason.is_not_applicable_for_category,
    },
    hidden: [ExpenseVATRegime.franchisee_supplier, ExpenseVATRegime.local_reverse_charge],
  };

  const zeroRateSubjectToVATTicketServiceCustomImportFees = {
    allowed: [ExpenseVATRegime.exempt_item],
    disallowed: {
      [ExpenseVATRegime.standard]: DisallowedVATRegimesReason.is_non_standard_vat_rate,
      [ExpenseVATRegime.foreign_vat]: DisallowedVATRegimesReason.is_non_foreign_vat_rate,
      [ExpenseVATRegime.local_reverse_charge]: DisallowedVATRegimesReason.is_not_applicable_for_category,
      [ExpenseVATRegime.eu_reverse_charge]: DisallowedVATRegimesReason.is_not_proper_invoice,
      [ExpenseVATRegime.extra_eu_reverse_charge]: DisallowedVATRegimesReason.is_not_proper_invoice_and_is_not_good_category,
      [ExpenseVATRegime.franchisee_supplier]: DisallowedVATRegimesReason.is_not_applicable_for_category,
    },
    hidden: [ExpenseVATRegime.franchisee_supplier, ExpenseVATRegime.local_reverse_charge],
  };

  const zeroRateSubjectToVATTicketGoodCustomImportFees = {
    allowed: [ExpenseVATRegime.exempt_item, ExpenseVATRegime.extra_eu_reverse_charge],
    disallowed: {
      [ExpenseVATRegime.standard]: DisallowedVATRegimesReason.is_non_standard_vat_rate,
      [ExpenseVATRegime.local_reverse_charge]: DisallowedVATRegimesReason.is_not_applicable_for_category,
      [ExpenseVATRegime.foreign_vat]: DisallowedVATRegimesReason.is_non_foreign_vat_rate,
      [ExpenseVATRegime.eu_reverse_charge]: DisallowedVATRegimesReason.is_not_proper_invoice,
      [ExpenseVATRegime.franchisee_supplier]: DisallowedVATRegimesReason.is_not_applicable_for_category,
    },
    hidden: [ExpenseVATRegime.franchisee_supplier, ExpenseVATRegime.local_reverse_charge],
  };

  const zeroRateNonSubjectToVATCustomImportFees = {
    allowed: [ExpenseVATRegime.exempt_item, ExpenseVATRegime.extra_eu_reverse_charge, ExpenseVATRegime.eu_reverse_charge],
    disallowed: {
      [ExpenseVATRegime.standard]: DisallowedVATRegimesReason.is_non_standard_vat_rate,
      [ExpenseVATRegime.local_reverse_charge]: DisallowedVATRegimesReason.is_not_applicable_for_category,
      [ExpenseVATRegime.foreign_vat]: DisallowedVATRegimesReason.is_non_foreign_vat_rate,
      [ExpenseVATRegime.franchisee_supplier]: DisallowedVATRegimesReason.is_not_applicable_for_category,
    },
    hidden: [ExpenseVATRegime.franchisee_supplier, ExpenseVATRegime.local_reverse_charge],
  };

  // ------------------------------------------------------------

  const exemptVATRegimes = {
    allowed: [ExpenseVATRegime.exempt_item],
    disallowed: {
      [ExpenseVATRegime.standard]: DisallowedVATRegimesReason.is_exempt_vat_category,
      [ExpenseVATRegime.local_reverse_charge]: DisallowedVATRegimesReason.is_exempt_vat_category,
      [ExpenseVATRegime.foreign_vat]: DisallowedVATRegimesReason.is_exempt_vat_category,
      [ExpenseVATRegime.eu_reverse_charge]: DisallowedVATRegimesReason.is_exempt_vat_category,
      [ExpenseVATRegime.extra_eu_reverse_charge]: DisallowedVATRegimesReason.is_exempt_vat_category,
      [ExpenseVATRegime.franchisee_supplier]: DisallowedVATRegimesReason.is_exempt_vat_category,
    },
    hidden: [],
  };

  const nullRateVATRegimes = {
    allowed: [ExpenseVATRegime.foreign_vat],
    disallowed: {
      [ExpenseVATRegime.standard]: DisallowedVATRegimesReason.is_non_standard_vat_rate,
      [ExpenseVATRegime.exempt_item]: DisallowedVATRegimesReason.is_non_zero_vat_rate,
      [ExpenseVATRegime.eu_reverse_charge]: DisallowedVATRegimesReason.is_non_zero_vat_rate,
      [ExpenseVATRegime.local_reverse_charge]: DisallowedVATRegimesReason.is_non_zero_vat_rate,
      [ExpenseVATRegime.franchisee_supplier]: DisallowedVATRegimesReason.is_non_zero_vat_rate,
      [ExpenseVATRegime.extra_eu_reverse_charge]: DisallowedVATRegimesReason.is_non_zero_vat_rate,
    },
    hidden: [],
  };

  const standardRateVATRegimes = {
    allowed: [ExpenseVATRegime.standard],
    disallowed: {
      [ExpenseVATRegime.exempt_item]: DisallowedVATRegimesReason.is_non_zero_vat_rate,
      [ExpenseVATRegime.eu_reverse_charge]: DisallowedVATRegimesReason.is_non_zero_vat_rate,
      [ExpenseVATRegime.local_reverse_charge]: DisallowedVATRegimesReason.is_non_zero_vat_rate,
      [ExpenseVATRegime.franchisee_supplier]: DisallowedVATRegimesReason.is_non_zero_vat_rate,
      [ExpenseVATRegime.extra_eu_reverse_charge]: DisallowedVATRegimesReason.is_non_zero_vat_rate,
      [ExpenseVATRegime.foreign_vat]: DisallowedVATRegimesReason.is_non_foreign_vat_rate,
    },
    hidden: [],
  };

  type Options = {
    vatRate: number | null | undefined;
    isInvoice: boolean;
    isSubjectToVAT: boolean;
    categoryType: ExpenseCategoryType | null | undefined;
    doesCategoryAllowVAT: boolean | null | undefined;
    isCustomForm: boolean;
    categoryId: string | null | undefined;
    country: AvailableCountries;
  };
  return ({ vatRate, isInvoice, isSubjectToVAT, categoryType, doesCategoryAllowVAT, isCustomForm, categoryId, country }: Options) => {
    const isImportFeesCustomForm = isCustomForm && (categoryId === cates.CATEGORY_IMPORT_FEES || categoryId === cates.CATEGORY_TAXES_AND_INSURANCE_IMPORT_FEES);

    if (doesCategoryAllowVAT === false) return exemptVATRegimes;

    if (vatRate == null) return nullRateVATRegimes;

    if (vatRate === 0) {
      if (isSubjectToVAT) {
        if (country === AvailableCountries.germany) {
          if (isImportFeesCustomForm) return zeroRateSubjectToVATInvoiceCustomImportFees;
          return zeroRateSubjectToVATInvoice;
        }
        if (isInvoice) {
          if (isImportFeesCustomForm) return zeroRateSubjectToVATInvoiceCustomImportFees;
          if (categoryType === ExpenseCategoryType.GOOD) return zeroRateSubjectToVATInvoiceGood;
          return zeroRateSubjectToVATInvoice;
        }
        if (categoryType === ExpenseCategoryType.SERVICE) {
          if (isImportFeesCustomForm) return zeroRateSubjectToVATTicketServiceCustomImportFees;
          return zeroRateSubjectToVATTicketService;
        }
        if (isImportFeesCustomForm) return zeroRateSubjectToVATTicketGoodCustomImportFees;
        return zeroRateSubjectToVATTicketGood;
      }
      if (isImportFeesCustomForm) return zeroRateNonSubjectToVATCustomImportFees;
      return zeroRateNonSubjectToVAT;
    }

    return standardRateVATRegimes;
  };
})();

type Guess = IExpenseV2['guessed_data'] | null | undefined;
type GuessOptions = {
  userCountry: AvailableCountries;
};
const userHistoryGuessSlugs = [
  ExpenseGuessedFromTypesEnum.userVATNumbersHistory,
  ExpenseGuessedFromTypesEnum.userSteuernummerHistory,
  ExpenseGuessedFromTypesEnum.userIBANHistory,
  ExpenseGuessedFromTypesEnum.userEmailHistory,
];

const aggregatedGuessSlugs = [
  ExpenseGuessedFromTypesEnum.aggregatedVATNumbers,
  ExpenseGuessedFromTypesEnum.aggregatedSteuernummers,
  ExpenseGuessedFromTypesEnum.aggregatedIBAN,
  ExpenseGuessedFromTypesEnum.aggregatedEmail,
];

const supportedSupplierGuessSlugs = userHistoryGuessSlugs.concat(aggregatedGuessSlugs, ExpenseGuessedFromTypesEnum.KBO);

const getSupplierGuessSlug = (guessedData: Guess, options: GuessOptions) => {
  const guess = guessedData?.supplier?.guessed_from;
  if (!guess) return null;
  if (!supportedSupplierGuessSlugs.includes(guess)) return null;
  return `expense.supplier.guess.${guess}`;
};

const getProfessionalPartGuessSlug = (guessedData: Guess, itemIndex: number, options: GuessOptions) => {
  const guess = guessedData?.items?.[itemIndex]?.professional_part;
  if (guess == null || guess >= 1) return null;
  return `expense.guess.professionalPart`;
};

const getItemRegimeGuessSlug = (guessedData: Guess, itemIndex: number, options: GuessOptions) => {
  const guess = guessedData?.items?.[itemIndex]?.vat_regime?.vat_regime;
  const evidences = guessedData?.items?.[itemIndex]?.vat_regime?.evidences;

  if (!guessedData) return null;
  if (!evidences || !evidences.length) return null;

  const reasons = evidences.reduce((acc, curr) => {
    acc[curr.reason] = true;
    return acc;
  }, {} as Record<ExpenseEvidenceReasonEnum, boolean>);

  const intraEuNumber = reasons.intra_eu_supplier_tax_number || reasons.intra_eu_supplier_vat_number; // || reasons.intra_eu_supplier_address;

  const localNumber =
    reasons.local_supplier_tax_number || reasons.local_supplier_steuernummer || reasons.local_supplier_vat_number || reasons.local_supplier_address;

  const extraEuNumber = reasons.extra_eu_supplier_vat_number || reasons.extra_eu_supplier_address;

  if (guess === ExpenseVATRegime.exempt_item) {
    // if (intraEuNumber) {
    //   return `expense.guess.vatRegime.intraSupplier`;
    // }
    return `expense.guess.default`;
  }

  if (guess === ExpenseVATRegime.eu_reverse_charge) {
    if (intraEuNumber && reasons.reverse_charge_note) {
      return `expense.guess.vatRegime.intraSupplier_reverseChargeNote`;
    }
    if (reasons.reverse_charge_note) {
      return `expense.guess.vatRegime.intraReverseChargeNote`;
    }
    if (intraEuNumber) {
      return `expense.guess.vatRegime.intraSupplier`;
    }
  }

  if (guess === ExpenseVATRegime.local_reverse_charge) {
    if (localNumber && reasons.reverse_charge_note) {
      return `expense.guess.vatRegime.localSupplier_reverseChargeNote`;
    }
    if (reasons.reverse_charge_note) return `expense.guess.vatRegime.localReverseChargeNote.${options.userCountry}`;
  }

  if (guess === ExpenseVATRegime.franchisee_supplier) {
    if (reasons.small_business_exemption_note) {
      return `expense.guess.vatRegime.franshiseeSupplier_businessExemption`;
    }
  }

  if (guess === ExpenseVATRegime.extra_eu_reverse_charge) {
    if (extraEuNumber) {
      return `expense.guess.vatRegime.extraSupplier`;
    }
    if (reasons.extra_eu_currency) {
      return `expense.guess.vatRegime.extraSupplier_extraCurrency`;
    }
  }

  return `expense.guess.default`;
};

const getItemCategoryGuessSlug = (guessedData: Guess, itemIndex: number, options: GuessOptions) => {
  const guess = guessedData?.items?.[itemIndex]?.guessed_category_from;
  if (!guess) return null;

  if (userHistoryGuessSlugs.includes(guess) && guessedData?.supplier?.name) {
    return `expense.guess.category.userHistory`;
  }

  if (guess === ExpenseGuessedFromTypesEnum.llm_CategoryOCR) {
    return `expense.guess.category.llm`;
  }

  return `expense.guess.default`;
};

const getIsInvoiceGuessSlug = (guessedData: Guess, options: GuessOptions) => {
  const guess = guessedData?.is_invoice;
  if (!guess) return null;
  return `expense.guess.isProperInvoice`;
};

export const getGuessReasons = (guessedData: Guess, options: GuessOptions) => {
  if (!guessedData) return null;

  return {
    date: {
      value: guessedData.date,
      reason: null,
    },
    due_date: {
      value: guessedData.due_date,
      reason: null,
    },
    payment_date: {
      value: guessedData.payment_date,
      reason: null,
    },
    is_invoice: {
      value: guessedData.is_invoice,
      reason: getIsInvoiceGuessSlug(guessedData, options),
    },
    supplier: {
      value: guessedData.supplier?.name,
      reason: getSupplierGuessSlug(guessedData, options),
    },
    currency: {
      value: guessedData.currency,
      reason: null,
    },
    items: guessedData.items?.map((item, index) => ({
      net_amount: {
        value: item.net_amount,
        reason: null,
      },
      vat_amount: {
        value: item.vat_amount,
        reason: null,
      },
      vat_rate: {
        value: item.vat_rate,
        reason: null,
      },
      vat_regime: {
        value: item.vat_regime?.vat_regime,
        reason: getItemRegimeGuessSlug(guessedData, index, options),
      },
      category: {
        value: item.category_id,
        reason: getItemCategoryGuessSlug(guessedData, index, options),
      },
      professional_part: {
        value: item.professional_part == null || item.professional_part >= 1 ? null : item.professional_part,
        reason: getProfessionalPartGuessSlug(guessedData, index, options),
      },
    })),
  };
};

//

export const getLatestTransactionsDate = <T extends Pick<Transaction, 'valueDate'>>(transactions: Nullable<Nullable<T>[]>) => {
  if (!Array.isArray(transactions) || !transactions.length) return null;
  const valids = transactions.filter((t) => !!t && t.valueDate) as T[];
  const sorted = valids.sort((a, b) => new Date(a.valueDate).getTime() - new Date(b.valueDate).getTime());
  const date = last(sorted)?.valueDate || null;
  if (!date) return null;
  return dayjs(date).format('YYYY-MM-DD');
};

type AssetsCheckArgs = { original: Nullable<PartialIExpenseV2>; updated: Nullable<PartialIExpenseV2> };
export const shouldWarnAboutAssetsChanges = ({ original, updated }: AssetsCheckArgs) => {
  if (!original || !updated) return false;
  const originalHasAsset = original.items?.some((item) => !!item.isAsset);

  if (!originalHasAsset) return false;

  const format = (expense: PartialIExpenseV2) => {
    return cleanEmptyValues_stringified({
      date: expense.expenseDateNumber,
      name: expense.supplier?.name,
      currency: expense.currency,
      rate: expense.currencyRate,
      proper: expense.isInvoice,
      items: expense.items?.map((item) => ({
        id: item._id,
        name: item.name,
        category: item.category?.id,
        amount: (item.currencyAmount || 0) * (expense.isCreditNote ? -1 : 1),
        rate: item.VATRate,
        regime: item.VATRegime,
        amortization: item.amortizationPeriod,
        asset: item.isAsset,
      })),
    });
  };

  return format(original) !== format(updated);
};
